import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import {
  getContactAccountRoles,
  setContactAccountRole,
  removeContactAccountRole,
  type ContactRole,
} from '../api/crmApi';

export const contactAccountRolesKeys = {
  all: ['contact-account-roles'] as const,
  list: (contactId: string) => [...contactAccountRolesKeys.all, contactId] as const,
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Unknown error';
}

export function useContactAccountRoles(contactId: string) {
  return useQuery({
    queryKey: contactAccountRolesKeys.list(contactId),
    queryFn: () => getContactAccountRoles(contactId),
    staleTime: 30 * 1000,
    enabled: !!contactId,
  });
}

export function useSetContactAccountRole(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { accountId: string; role: ContactRole; ordinal?: number; notes?: string }) =>
      setContactAccountRole(contactId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactAccountRolesKeys.list(contactId) });
      // Account-side reads (KeyContactsPanel, accounts list cells) also need refresh.
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['account-members-picker'] });
      toast.success(i18next.t('crm:roles.toast.assigned'));
    },
    onError: (error) => {
      toast.error(i18next.t('crm:roles.toast.failedAssign'), { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveContactAccountRole(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, role }: { accountId: string; role: ContactRole }) =>
      removeContactAccountRole(contactId, accountId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactAccountRolesKeys.list(contactId) });
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['account-members-picker'] });
      toast.success(i18next.t('crm:roles.toast.removed'));
    },
    onError: (error) => {
      toast.error(i18next.t('crm:roles.toast.failedRemove'), { description: getErrorMessage(error) });
    },
  });
}
